interface ShimmerBlockProps {
  className?: string;
  delayMs?: number;
}

const ShimmerBlock = ({ className, delayMs = 0 }: ShimmerBlockProps) => (
  <div
    className={`animate-pulse rounded bg-muted dark:bg-muted-foreground/20 ${className}`}
    style={{ animationDelay: `${delayMs}ms` }}
  />
);

const ChangePillSkeleton = ({ delayMs = 0 }: { delayMs?: number }) => (
  <div className="flex items-center gap-2">
    <ShimmerBlock className="h-2.5 w-2.5 rounded-full" delayMs={delayMs} />
    <ShimmerBlock className="h-3.5 w-16 rounded-full" delayMs={delayMs} />
  </div>
);

const TotalBoxSkeleton = ({
  toneClass,
  delayMs,
}: {
  toneClass: string;
  delayMs: number;
}) => (
  <div className={`flex min-w-[96px] flex-col overflow-hidden rounded-lg border ${toneClass}`}>
    <div className="px-2 py-1">
      <ShimmerBlock className="mx-auto h-3.5 w-14" delayMs={delayMs} />
    </div>
    <div className="flex items-center justify-center bg-background px-2 py-1">
      <ShimmerBlock className="h-6 w-20" delayMs={delayMs} />
    </div>
  </div>
);

const FooterButtonSkeleton = ({ delayMs = 0 }: { delayMs?: number }) => (
  <div className="flex w-full max-w-sm items-center justify-between rounded-lg border border-border bg-muted p-3">
    <div className="space-y-1.5">
      <ShimmerBlock className="h-5 w-44" delayMs={delayMs} />
      <ShimmerBlock className="h-4 w-28" delayMs={delayMs} />
    </div>
    <ShimmerBlock className="h-5 w-5 rounded-full" delayMs={delayMs} />
  </div>
);

const ChartScaffoldSkeleton = () => (
  <div className="relative h-[300px] w-full overflow-hidden">
    <div className="pointer-events-none absolute -top-1 left-1/2 z-[1] h-4 w-4 -translate-x-1/2 rounded-full border-4 border-background bg-muted-foreground/50" />
    <div className="pointer-events-none absolute bottom-[26px] left-1/2 top-0 z-[1] w-[2px] -translate-x-1/2 bg-muted-foreground/30" />

    <div className="absolute inset-x-6 top-14">
      <ShimmerBlock className="h-1.5 w-full rounded-full" delayMs={30} />
    </div>
    <div className="absolute inset-x-10 top-28">
      <ShimmerBlock className="h-1.5 w-[88%] rounded-full" delayMs={90} />
    </div>
    <div className="absolute inset-x-8 top-44">
      <ShimmerBlock className="h-1.5 w-[92%] rounded-full" delayMs={150} />
    </div>

    <div className="absolute bottom-14 left-0 right-0 grid grid-cols-7 px-6">
      {[0, 1, 2, 3, 4, 5, 6].map((idx) => (
        <div key={idx} className="flex justify-center">
          <ShimmerBlock
            className={`h-3.5 ${idx === 3 ? 'w-8' : 'w-6'} rounded-full`}
            delayMs={idx * 40}
          />
        </div>
      ))}
    </div>
  </div>
);

function CashFlowDetailsSkeleton() {
  return (
    <>
      <div className="mb-4">
        <div className="flex w-full flex-col items-center justify-center p-8 pb-0">
          <div className="w-full max-w-sm">
            <ShimmerBlock className="h-7 w-40" />
            <div className="mt-1 flex items-center gap-4">
              <ChangePillSkeleton />
              <ChangePillSkeleton delayMs={70} />
              <ChangePillSkeleton delayMs={140} />
            </div>
            <ShimmerBlock className="mt-1 h-3.5 w-56" delayMs={120} />
          </div>
        </div>

        <div className="flex h-[400px] flex-col justify-start pt-4">
          <div className="mx-auto flex items-center justify-center space-x-2 rounded-2xl border border-border bg-muted p-2 text-center">
            <TotalBoxSkeleton
              toneClass="border-emerald-500/30 bg-emerald-500/10"
              delayMs={40}
            />
            <TotalBoxSkeleton toneClass="border-border bg-zinc-400/10" delayMs={100} />
            <TotalBoxSkeleton toneClass="border-rose-500/30 bg-rose-500/10" delayMs={160} />
          </div>

          <ChartScaffoldSkeleton />
        </div>
      </div>

      <div className="flex flex-col items-center space-y-3 bg-background p-4 pt-0">
        <FooterButtonSkeleton delayMs={20} />
        <FooterButtonSkeleton delayMs={80} />
        <FooterButtonSkeleton delayMs={140} />
      </div>
    </>
  );
}

export default CashFlowDetailsSkeleton;
